import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { suppliersApi, billsApi, whtApi, type Supplier, type SupplierInput, type WhtRule } from "../lib/business";
import { coaApi } from "../lib/tenant";

export function SuppliersPage() {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Supplier | null>(null);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["suppliers", q],
    queryFn: () => suppliersApi.list(q),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["suppliers"] });
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Suppliers</div>
          <h1 className="stage-title">
            People who <em>bill you.</em>
          </h1>
          <p className="stage-sub">
            Vendors, contractors, utility providers. Used on bills and WHT.
          </p>
        </div>
      </div>

      <div className="detail-2col" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24, alignItems: "start" }}>
        <div className="card card-pad-lg">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <input
              className="form-input"
              style={{ maxWidth: 280 }}
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button
              className="btn btn-line"
              style={{ fontSize: 12, padding: "8px 14px" }}
              onClick={() => setEditing({} as Supplier)}
            >
              + New supplier
            </button>
          </div>

          {isLoading ? (
            <div style={{ color: "var(--ink-3)" }}>Loading…</div>
          ) : (data?.suppliers.length ?? 0) === 0 ? (
            <div
              className="card"
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: "var(--ink-3)",
                background: "var(--paper-2)",
                borderStyle: "dashed",
              }}
            >
              No suppliers yet.
            </div>
          ) : (
            <div className="table-cards table-scroll">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Contact</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {data?.suppliers.map((s) => {
                  const initials = s.name
                    .split(/\s+/)
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                  return (
                    <tr key={s.id}>
                      <td style={td} data-fullwidth data-label="Name">
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <div className="avatar av-3">{initials}</div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{s.name}</div>
                            {s.address && (
                              <div style={{ fontSize: 12, color: "var(--ink-4)" }}>{s.address}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={td} data-label="Contact">
                        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                          {s.email ?? "—"}
                          <br />
                          {s.phone ?? ""}
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: "right" }} data-fullwidth>
                        <div style={{ display: "inline-flex", gap: 4 }}>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 8px", fontSize: 11 }}
                            onClick={() => { setEditing(s); window.location.hash = "outstanding"; }}
                            title="See unpaid bills for this supplier"
                          >
                            Outstanding
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 10px", fontSize: 12 }}
                            onClick={() => setEditing(s)}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* `key` forces a remount whenever editing changes — without this,
            useState(initial) only runs once and the draft keeps the previously
            saved supplier's data across "+ New supplier" clicks. */}
        <SupplierForm
          key={editing ? (editing.id ?? "new") : "idle"}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      </div>
    </>
  );
}

// SL VAT registration numbers look like `<9-digit TIN>-7000` (or similar
// branch suffix). Strip the trailing hyphen-suffix to recover the TIN.
// Returns null when the VAT doesn't have a parseable suffix.
function vatToTin(vat: string | null | undefined): string | null {
  const v = (vat || "").trim();
  if (!v) return null;
  const m = v.match(/^(.+)-\d+\s*$/);
  return m ? m[1].trim() : null;
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 0",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "12px 0",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

function SupplierForm({
  editing,
  onClose,
  onSaved,
}: {
  editing: Supplier | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial: SupplierInput = editing
    ? {
        name: editing.name ?? "",
        email: editing.email ?? "",
        phone: editing.phone ?? "",
        address: editing.address ?? "",
        vatRegistrationNo: editing.vatRegistrationNo ?? "",
        defaultExpenseAccountId: editing.defaultExpenseAccountId ?? null,
        isResident: editing.isResident ?? true,
        isIndividual: editing.isIndividual ?? true,
        whtCategoryCode: editing.whtCategoryCode ?? null,
        tin: editing.tin ?? "",
        nicOrPassport: editing.nicOrPassport ?? "",
      }
    : { name: "", email: "", phone: "", address: "", vatRegistrationNo: "", defaultExpenseAccountId: null, isResident: true, isIndividual: true };

  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Scroll into view when the row sets a hash (Outstanding link).
  useEffect(() => {
    if (!editing?.id) return;
    const target = window.location.hash.replace("#", "");
    if (target !== "outstanding") return;
    const tick = window.setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }, 200);
    return () => window.clearTimeout(tick);
  }, [editing?.id]);

  const { data: whtRulesData } = useQuery({
    queryKey: ["wht-rules"],
    queryFn: () => whtApi.rules(),
    staleTime: 5 * 60 * 1000,
  });
  const whtRules: WhtRule[] = whtRulesData?.rules ?? [];

  // Chart of Accounts — used to drive the "Default expense account" picker.
  // We restrict to expense-type accounts so the dropdown is reasonable.
  const { data: coaData } = useQuery({
    queryKey: ["coa", "mine"],
    queryFn: () => coaApi.mine(),
    staleTime: 5 * 60 * 1000,
  });
  const expenseAccounts = (coaData?.accounts ?? []).filter((a) => a.type === "expense");

  if (!editing) {
    return (
      <div className="card card-pad-lg" style={{ background: "var(--paper-2)" }}>
        <div className="stage-eyebrow">New supplier</div>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 8 }}>
          Click "+ New supplier" to add a vendor, or pick a row to edit.
        </p>
      </div>
    );
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    // Client-side VAT→TIN guard so the user doesn't pay the round-trip.
    if ((draft.vatRegistrationNo || "").trim() && !(draft.tin || "").trim()) {
      setErr("TIN is required when a VAT registration no. is set. The TIN is the digits before the -7000 suffix.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const body: SupplierInput = {
        name: draft.name,
        email: draft.email || null,
        phone: draft.phone || null,
        address: draft.address || null,
        vatRegistrationNo: draft.vatRegistrationNo || null,
        defaultExpenseAccountId: draft.defaultExpenseAccountId || null,
        isResident: draft.isResident,
        isIndividual: draft.isIndividual,
        whtCategoryCode: draft.whtCategoryCode || null,
        tin: draft.tin || null,
        nicOrPassport: draft.nicOrPassport || null,
      };
      if (editing!.id) await suppliersApi.update(editing!.id, body);
      else await suppliersApi.create(body);
      onSaved();
    } catch (e) {
      // Surface backend-side dedup conflicts so the user knows WHY their save
      // was rejected (instead of the generic "Could not save"). The api
      // wrapper throws an Error whose .message contains the JSON body for
      // non-2xx responses; parse defensively.
      const raw = e instanceof Error ? e.message : String(e);
      let parsed: { error?: string; field?: string; message?: string } | null = null;
      try { parsed = JSON.parse(raw); } catch { /* not JSON */ }
      if (parsed?.error === "duplicate_tax_id") {
        setErr(parsed.message ?? "Another supplier already has this tax ID.");
      } else {
        setErr("Could not save. Check the details.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card-pad-lg form-side-panel" style={{ position: "sticky", top: 80 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div className="stage-eyebrow">{editing.id ? "Edit" : "New"}</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500 }}>
            {editing.id ? editing.name : "Add supplier"}
          </div>
        </div>
        <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 18 }} onClick={onClose}>
          ×
        </button>
      </div>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="form-field">
          <label className="form-label">Name</label>
          <input className="form-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
        </div>
        <div className="form-field">
          <label className="form-label">Email</label>
          <input
            className="form-input"
            type="email"
            value={draft.email ?? ""}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Phone</label>
          <input
            className="form-input"
            value={draft.phone ?? ""}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Address</label>
          <input
            className="form-input"
            value={draft.address ?? ""}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">VAT registration no.</label>
          <input
            className="form-input"
            value={draft.vatRegistrationNo ?? ""}
            placeholder="123456789-7000"
            onChange={(e) => {
              const vat = e.target.value;
              const next = { ...draft, vatRegistrationNo: vat };
              // Auto-derive TIN from VAT (everything before the LAST hyphen).
              // Only auto-fill when the TIN field is empty so we don't trample
              // an explicit value the user has typed.
              const derived = vatToTin(vat);
              if (derived && !((draft.tin || "").trim())) next.tin = derived;
              setDraft(next);
            }}
          />
          <p style={{ color: "var(--ink-4)", fontSize: 11, margin: "4px 2px 0" }}>
            Format: <code>&lt;TIN&gt;-7000</code> · we'll auto-fill the TIN below.
          </p>
        </div>
        <div className="form-field">
          <label className="form-label">Default expense account</label>
          <select
            className="form-input"
            value={draft.defaultExpenseAccountId ?? ""}
            onChange={(e) => setDraft({ ...draft, defaultExpenseAccountId: e.target.value || null })}
          >
            <option value="">— Pick on each bill —</option>
            {expenseAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} · {a.name}
              </option>
            ))}
          </select>
          <p style={{ color: "var(--ink-4)", fontSize: 11, margin: "4px 2px 0" }}>
            New bills for this supplier will default to this account.
          </p>
        </div>

        {/* WHT (Withholding Tax) section — drives default rate on bills + certificate */}
        <div style={{ marginTop: 6, paddingTop: 14, borderTop: "0.5px solid var(--line)" }}>
          <div className="stage-eyebrow" style={{ marginBottom: 8 }}>Withholding tax</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={draft.isResident ?? true}
                onChange={(e) => setDraft({ ...draft, isResident: e.target.checked })}
              />
              Resident
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={draft.isIndividual ?? true}
                onChange={(e) => setDraft({ ...draft, isIndividual: e.target.checked })}
              />
              Individual
            </label>
          </div>
          <div className="form-field" style={{ marginTop: 8 }}>
            <label className="form-label">Default WHT category</label>
            <select
              className="form-input"
              value={draft.whtCategoryCode ?? ""}
              onChange={(e) => setDraft({ ...draft, whtCategoryCode: e.target.value || null })}
            >
              <option value="">— None —</option>
              {whtRules.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">
              TIN
              {(draft.vatRegistrationNo || "").trim() && !(draft.tin || "").trim() && (
                <span style={{ color: "var(--danger, #b91c1c)", marginLeft: 6, fontWeight: 600 }}>· required (VAT entered)</span>
              )}
            </label>
            <input
              className="form-input"
              value={draft.tin ?? ""}
              onChange={(e) => setDraft({ ...draft, tin: e.target.value })}
              style={{
                borderColor: (draft.vatRegistrationNo || "").trim() && !(draft.tin || "").trim()
                  ? "var(--danger, #b91c1c)" : undefined,
              }}
            />
          </div>
          <div className="form-field">
            <label className="form-label">NIC / passport</label>
            <input
              className="form-input"
              value={draft.nicOrPassport ?? ""}
              onChange={(e) => setDraft({ ...draft, nicOrPassport: e.target.value })}
            />
          </div>
        </div>

        {err && <div className="banner banner-error">{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
            {saving ? "Saving…" : editing.id ? "Save" : "Add"}
          </button>
          {editing && editing.id && (
            <button
              type="button"
              className="btn btn-line"
              onClick={async () => {
                if (editing && confirm(`Remove ${editing.name}?`)) {
                  await suppliersApi.remove(editing.id);
                  onSaved();
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      </form>

      {/* Outstanding bills for this supplier — surfaces the running liability
          inline so Shamil can see "how much we owe this supplier" without
          jumping to the Bills page. */}
      {editing.id && <div id="outstanding"><OutstandingBillsPanel supplierId={editing.id} /></div>}
    </div>
  );
}

function OutstandingBillsPanel({ supplierId }: { supplierId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["supplier-outstanding", supplierId],
    queryFn: () => billsApi.list("outstanding", supplierId),
    staleTime: 30_000,
  });
  const rows = data?.bills ?? [];
  const outstandingCents = rows.reduce(
    (s, r) => s + (Number(r.total_cents) - Number(r.paid_cents)),
    0,
  );
  const fmt = (cents: number) =>
    `LKR ${(cents / 100).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div style={{ marginTop: 14, padding: 14, background: "var(--paper-2)", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 14 }}>
          Outstanding bills (what we owe)
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: outstandingCents > 0 ? "#b91c1c" : "var(--ink-3)" }}>
          {fmt(outstandingCents)}
        </div>
      </div>
      {isLoading ? (
        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>None — all bills are paid or voided.</div>
      ) : (
        <table style={{ width: "100%", fontSize: 12 }}>
          <tbody>
            {rows.map((r) => {
              const balance = Number(r.total_cents) - Number(r.paid_cents);
              const overdue = new Date(r.due_date) < new Date() && r.status !== "paid";
              return (
                <tr key={r.id} style={{ borderTop: "0.5px solid var(--line)" }}>
                  <td style={{ padding: "6px 0" }}>
                    <a href={`/clearbooks/bills/${r.id}`} style={{ fontWeight: 500 }}>
                      {r.bill_number}
                    </a>
                    <div style={{ fontSize: 10, color: overdue ? "#b91c1c" : "var(--ink-4)" }}>
                      Due {r.due_date}{overdue ? " · overdue" : ""}
                    </div>
                  </td>
                  <td style={{ padding: "6px 0", textAlign: "right" }}>
                    <div>{fmt(balance)}</div>
                    <div style={{ fontSize: 10, color: "var(--ink-4)" }}>
                      of {fmt(Number(r.total_cents))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
